import { useVideoGallery } from '@/components/blocks/ProductDetails/context/VideoGalleryContext';
import { PlayVideoButton } from '@/components/blocks/VideoDisplay/PlayVideoButton';
import {
	iframeContainerSX,
	iframeStyle,
	mainDisplayContainerSX,
	mainImageOverlaySX,
	mainImageSX,
	playerWrapperSX,
	playOverlaySX,
} from '@/components/blocks/VideoDisplay/styles/videoStyle';
import { Attachment } from '@/data/types/Product';
import { getErrorImg, getVideoThumbnail, getVideoUrl, getYoutubeId } from '@/utils/video';
import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

interface MainVideoPlayerProps {
	videos: Attachment[];
	handleChangeVideo: (videoData: Attachment) => void;
}

export const MainVideoPlayer: FC<MainVideoPlayerProps> = ({ videos, handleChangeVideo }) => {
	const { actualVideo, isVideoPlayerActive, setIsVideoPlayerActive } = useVideoGallery();
	const [swiper, setSwiper] = useState<SwiperType | null>(null);

	const currentIndex = videos.findIndex(
		(video) => video.attachmentAssetPath === actualVideo?.attachmentAssetPath
	);

	useEffect(() => {
		if (swiper && currentIndex >= 0 && swiper.activeIndex !== currentIndex) {
			swiper.slideTo(currentIndex);
		}
	}, [swiper, currentIndex]);

	const onSlideChange = useCallback(
		(swiperInstance: SwiperType) => {
			const video = videos[swiperInstance.activeIndex];
			if (video && video.attachmentAssetPath !== actualVideo?.attachmentAssetPath) {
				handleChangeVideo(video);
				// Land on the poster, not an autoplaying iframe: an iframe over the
				// active slide would swallow the next swipe's touch gesture.
				setIsVideoPlayerActive(false);
			}
		},
		[videos, actualVideo, handleChangeVideo, setIsVideoPlayerActive]
	);

	const activatePlayer = useCallback(() => {
		setIsVideoPlayerActive(true);
	}, [setIsVideoPlayerActive]);

	return (
		<Box sx={playerWrapperSX}>
			<Box id="mediaImgMainDislay" sx={mainDisplayContainerSX}>
				<Swiper
					onSwiper={setSwiper}
					onSlideChange={onSlideChange}
					initialSlide={currentIndex >= 0 ? currentIndex : 0}
					slidesPerView={1}
					id="mediaVideoMainDisplay"
				>
					{videos.map((video) => {
						const videoId = getYoutubeId(video.attachmentAssetPath || '');
						if (!videoId) return null;

						const isActiveSlide = video.attachmentAssetPath === actualVideo?.attachmentAssetPath;
						const mainImageUrl = getVideoThumbnail(videoId);
						const embedUrl = getVideoUrl(videoId);

						return (
							<SwiperSlide key={video.attachmentAssetPath}>
								{isActiveSlide && isVideoPlayerActive ? (
									<Box sx={iframeContainerSX}>
										<iframe
											src={embedUrl}
											frameBorder="0"
											allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
											allowFullScreen
											style={iframeStyle}
											key={videoId}
											id="mediaVideoiFrame"
										></iframe>
									</Box>
								) : (
									<Box sx={mainImageOverlaySX} onClick={activatePlayer}>
										<Box
											component="img"
											src={mainImageUrl}
											alt=""
											sx={mainImageSX}
											className="thumbnail-image"
											onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
												e.currentTarget.src = getErrorImg(1600, 900, '4f46e5', 'fff', 'Error');
											}}
										/>
										<Box sx={playOverlaySX} className="play-overlay">
											<PlayVideoButton size={72} className="text-white opacity-90 play-button" />
										</Box>
									</Box>
								)}
							</SwiperSlide>
						);
					})}
				</Swiper>
			</Box>
		</Box>
	);
};
